import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getToken, removeToken, getStoredUser } from './auth';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const ACTIVE_ORG_KEY = 'norgas_active_org';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and active org header
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Always inject X-Active-Org-Id so the backend resolves the correct per-org role
    try {
      const stored = sessionStorage.getItem(ACTIVE_ORG_KEY);
      if (stored && !config.headers['X-Active-Org-Id']) {
        const { id: activeOrgId } = JSON.parse(stored) as { id: string };
        if (activeOrgId) {
          config.headers['X-Active-Org-Id'] = activeOrgId;
        }
      } else if (!config.headers['X-Active-Org-Id']) {
        // No stored org — use the user's primary org from JWT
        const userJson = getStoredUser();
        if (userJson) {
          const { organizationId } = JSON.parse(userJson) as { organizationId: string };
          if (organizationId) {
            config.headers['X-Active-Org-Id'] = organizationId;
          }
        }
      }
    } catch {
      // ignore — non-blocking
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);
